import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  createUserWithEmailAndPassword,
  GoogleAuthProvider,
  signInWithPopup,
  sendEmailVerification,
} from 'firebase/auth';
import { auth } from '../firebase';
import { authErrorMessage, isSilentAuthError } from '../authMessages';
import AuthLayout from '../components/AuthLayout';
import { toast } from 'react-toastify';
import GoogleLogo from '../images/google-g.webp';
import {
  Box,
  Button,
  Divider,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { useTheme } from '@mui/material/styles';
import { useSeo } from '../seo';

const Signup = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const handleSignup = async (e) => {
    e.preventDefault();

    // Add password confirmation
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    const address = email.trim();
    try {
      const { user } = await createUserWithEmailAndPassword(auth, address, password);
      // Until the address is verified, Firebase removes the password from a
      // Gmail account the first time anyone uses "Sign in with Google" with
      // it, and the password then stops working. Verifying prevents that.
      // Nothing waits on it: the account is usable straight away.
      sendEmailVerification(user, { url: `${window.location.origin}/home` }).catch((err) =>
        console.warn('Could not send the verification email:', err)
      );
      toast.success('Signup successful! We have emailed you a link to confirm your address.');
      navigate('/home');
    } catch (error) {
      if (error.code === 'auth/email-already-in-use') {
        toast.info(authErrorMessage(error));
        navigate('/login', { state: { email: address } });
      } else {
        toast.error(authErrorMessage(error));
      }
    }
  };

  const handleGoogleSignup = async () => {
    const provider = new GoogleAuthProvider();
    try {
      await signInWithPopup(auth, provider);
      toast.success('Google sign-in successful!');
      navigate('/home');
    } catch (error) {
      if (error.code === 'auth/account-exists-with-different-credential') {
        toast.info(authErrorMessage(error));
        navigate('/login');
      } else if (!isSilentAuthError(error)) {
        toast.error(authErrorMessage(error));
      }
    }
  };

  useSeo({ title: 'Create an account | Crestancia', path: '/signup', noindex: true });

  const theme = useTheme();
  const accent = theme.palette.primary.main;

  // Shown only once the field has something in it, so the form does not scold
  // anyone before they have finished typing.
  const mismatch = confirmPassword.length > 0 && password !== confirmPassword;

  const passwordAdornment = (
    <InputAdornment position="end">
      <IconButton
        onClick={() => setShowPassword((shown) => !shown)}
        aria-label={showPassword ? 'Hide password' : 'Show password'}
        edge="end"
        size="small"
      >
        {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
      </IconButton>
    </InputAdornment>
  );

  return (
    <AuthLayout title="Create your account" subtitle="One account to order and follow your deliveries.">
      <Box component="form" onSubmit={handleSignup} sx={{ width: '100%' }}>
        <TextField
          variant="outlined"
          required
          fullWidth
          id="email"
          label="Email Address"
          name="email"
          type="email"
          autoComplete="email"
          autoFocus
          inputProps={{ autoCapitalize: 'none', autoCorrect: 'off', spellCheck: false }}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          sx={{ mb: 2.5 }}
        />
        <TextField
          variant="outlined"
          required
          fullWidth
          name="password"
          label="Password"
          type={showPassword ? 'text' : 'password'}
          id="password"
          autoComplete="new-password"
          inputProps={{ autoCapitalize: 'none', autoCorrect: 'off', spellCheck: false }}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          InputProps={{ endAdornment: passwordAdornment }}
          sx={{ mb: 2.5 }}
        />
        <TextField
          variant="outlined"
          required
          fullWidth
          name="confirmPassword"
          label="Confirm Password"
          type={showPassword ? 'text' : 'password'}
          id="confirmPassword"
          autoComplete="new-password"
          inputProps={{ autoCapitalize: 'none', autoCorrect: 'off', spellCheck: false }}
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          error={mismatch}
          helperText={mismatch ? 'Passwords do not match' : ' '}
        />

        <Button
          type="submit"
          fullWidth
          variant="contained"
          color="primary"
          disableElevation
          sx={{
            mt: 2.5,
            py: 1.5,
            borderRadius: 999,
            fontWeight: 700,
            fontSize: '1rem',
            textTransform: 'none',
          }}
        >
          Create account
        </Button>

        <Divider sx={{ my: 3 }}>
          <Typography
            sx={{
              px: 1,
              fontSize: '0.72rem',
              fontWeight: 700,
              letterSpacing: '0.14em',
              color: 'text.secondary',
            }}
          >
            OR
          </Typography>
        </Divider>

        <Button
          fullWidth
          variant="outlined"
          onClick={handleGoogleSignup}
          sx={{
            py: 1.4,
            borderRadius: 999,
            gap: 1.5,
            fontWeight: 600,
            textTransform: 'none',
            color: 'text.primary',
            borderColor: 'divider',
            '&:hover': { borderColor: 'text.disabled', backgroundColor: 'action.hover' },
          }}
        >
          <Box component="img" src={GoogleLogo} alt="" sx={{ height: 20, width: 20 }} />
          Sign up with Google
        </Button>
      </Box>

      <Typography sx={{ mt: 3, color: 'text.secondary', fontSize: '0.8rem', lineHeight: 1.6 }}>
        We use your email address to sign you in and to send your order confirmations. See our{' '}
        <Box component={Link} to="/privacy" sx={{ color: accent, fontWeight: 600 }}>
          privacy notice
        </Box>
        .
      </Typography>

      <Typography sx={{ mt: 3, color: 'text.secondary', fontSize: '0.92rem' }}>
        Already have an account?{' '}
        <Box
          component={Link}
          to="/login"
          sx={{ color: accent, textDecoration: 'none', fontWeight: 700 }}
        >
          Sign in
        </Box>
      </Typography>
    </AuthLayout>
  );
};

export default Signup;
